import _ from 'lodash';
import moment from 'moment';

import Ajax from '@/utils/Ajax';

// Coalesces a burst of invoice-identity switches into one request.
const ACCOUNT_AVAILABILITY_DEBOUNCE_MS = 250;
const BUCKET_MONTH_FORMAT = 'YYYY-MM';
const BUCKET_MONTH_DISPLAY_FORMAT = 'MMMM YYYY';

// OB-6 — FAIL OPEN, ALWAYS. Anything other than a well-formed answer — a network error, a timeout, a
// 404 while the endpoint is still being rolled out, a body we cannot parse — must leave Account
// payment OFFERED (and, OB-35, the out-of-policy request sendable). The server-side gate is
// authoritative and refuses on its own; withholding a control because a courtesy read failed would
// turn an outage into a customer-visible refusal. This is the same default `isAccountPaymentAvailable`
// encodes for an absent field.
//
// The endpoint also returns `reason` ('THRESHOLD' | 'MANUAL_BLOCK' | 'INVALID_IDENTITY'). It is
// DELIBERATELY not read here and must never reach the screen: a customer must not learn that a human
// put their account on hold — that is an internal finance action and leaking it invites the wrong
// support conversation.
export const AVAILABLE = { available: true, bucket_month: null };

export function parseAccountPaymentAvailability(response) {
    if (!response || typeof response.available !== 'boolean') {
        return AVAILABLE;
    }

    const bucket_month = response.bucket_month;

    return {
        available: response.available,
        bucket_month: typeof bucket_month === 'string' && bucket_month ? bucket_month : null,
    };
}

// The display month the refusal is about, or null when we cannot name one — an unparseable month must
// fall back to the no-month copy so "Invalid date" can never reach the screen.
export function getAccountPaymentUnavailableMonthLabel(availability) {
    const bucket_month = availability && availability.bucket_month;
    const month = bucket_month ? moment(bucket_month, BUCKET_MONTH_FORMAT) : null;

    return month && month.isValid() ? month.format(BUCKET_MONTH_DISPLAY_FORMAT) : null;
}

/**
 * BUG 51 — is the customer still to choose WHICH of their company's invoice entities this booking is
 * billed to?
 *
 * The cap is PER ENTITY, so until one is chosen there is no cap to ask about. A read sent with no
 * `invoice_id` is answered against the resolver's POSITIONAL DEFAULT (the company-level attachment,
 * then the agency walk) — not necessarily the entity that will be invoiced — so a definite
 * `available: false` from it would withhold a control and name a month over a cap the customer never
 * selected. That is a refusal we have NOT established, and fail-open owns it: no question asked, no
 * opinion held, and `/pay`'s gate stays the authority.
 *
 * Only a company that OFFERS a choice has anything unresolved: with one entity or none there is no
 * selector, nothing to pick, and the positional default IS the answer.
 */
export function companyOffersInvoiceIdentityChoice(company, private_travel) {
    const invoice_ids = company && company.invoice_ids;

    return !private_travel && Array.isArray(invoice_ids) && !!invoice_ids[0] && !!invoice_ids[1];
}

// The company's credit-line kill switch, read exactly as the server reads it
// (`isCreditLineDisabledForCompany`): anything but a literal `false` is OFF. An off company's
// availability answer is always "available", so there is nothing to ask.
export function isCreditLineOffForCompany(company) {
    return _.get(company, 'settings.credit_line_disabled') !== false;
}

export function isInvoiceIdentityUnresolved({ company, private_travel, invoice_id }) {
    return companyOffersInvoiceIdentityChoice(company, private_travel) && !invoice_id;
}

/**
 * BUG 62 (reopened) — may an in-flight read hold checkout's submit button?
 *
 * ONLY while Account payment is the selected method: the cap governs Account payment alone, so a card
 * (or any other) booking has nothing to wait for and must never be held or told we are checking a
 * credit line. Derived at render from the CURRENT method, so switching onto Account payment during a
 * read holds at once and switching away releases at once — no second flag to keep in step.
 */
export function isHeldByAccountPaymentAvailability({ pending, account_payment_selected }) {
    return pending === true && account_payment_selected === true;
}

/**
 * Debounced, ticketed reader of `GET /credit-line/account-payment-availability`.
 *
 * ONE implementation for every screen that asks the question (checkout's payment methods, and the
 * out-of-policy request button) — the fail-open default and the out-of-order protection are the
 * load-bearing parts, and two copies of them would eventually disagree.
 *
 * `onAvailability` is called with `null` when the identity being asked about CHANGES (the previous
 * entity's answer does not describe the new one and would name the wrong month), then with the parsed
 * answer. `null` means "no opinion yet" and leaves the caller's pre-OB-6 behaviour untouched.
 *
 * BUG 55 — `onPending` is a SEPARATE channel, called with `true` the moment a question is asked about a
 * new identity and with `false` the moment that question is answered (either way). It is separate on
 * purpose: "we are still asking" and "the answer is no" are different claims, and folding the first
 * into the `onAvailability` value would let a consumer render the cap-exceeded copy over a verdict
 * nobody has given. Both current consumers (checkout's Complete Booking — BUG 62 — and the out-of-policy
 * Request button) pass it; a consumer that passes no `onPending` sees exactly the old behaviour.
 */
export class AccountPaymentAvailabilityFetcher {
    constructor(onAvailability, { onPending } = {}) {
        this._onAvailability = onAvailability;
        this._onPending = onPending;
        this._key = null;
        this._timer = null;
        this._request_id = 0;
        this._pending = false;
        this._disposed = false;
    }

    // Edge-triggered, so a consumer can hold it in state without a setState per schedule() call.
    _setPending(pending) {
        if (this._pending === pending) {
            return;
        }

        this._pending = pending;

        if (this._onPending) {
            this._onPending(pending);
        }
    }

    // Identity of the question being asked: which booking, charged to which invoice entity. No
    // reservation id means it is still initializing and there is nothing to ask about.
    //
    // BUG 51 — `identity_unresolved` says the screen still owes us a payment entity (see
    // isInvoiceIdentityUnresolved). Nothing is asked, and any earlier answer is withdrawn: it was
    // about a different entity and must not outlive it.
    //
    // `credit_line_off` (isCreditLineOffForCompany) — the server would answer "available" without
    // looking, so nothing is asked and no opinion is held: the same exit as an unresolved identity.
    schedule(reservation_id, invoice_id, { identity_unresolved = false, credit_line_off = false } = {}) {
        if (this._disposed || !reservation_id) {
            return;
        }

        if (identity_unresolved || credit_line_off) {
            this._forget();
            return;
        }

        const key = `${reservation_id}|${invoice_id || ''}`;
        if (key === this._key) {
            return;
        }

        const had_previous_key = !!this._key;

        this._key = key;
        clearTimeout(this._timer);

        // BUG 62 — VOID THE IN-FLIGHT TICKET NOW, not when the new request leaves. `_fetch` only takes
        // a ticket after the debounce, so an answer about the previous identity that lands inside that
        // window would otherwise still hold the latest ticket: it would clear pending and be reported
        // as the verdict for an identity nobody has asked about yet.
        this._request_id++;

        this._timer = setTimeout(
            () => this._fetch(reservation_id, invoice_id),
            ACCOUNT_AVAILABILITY_DEBOUNCE_MS,
        );

        // BUG 55 — PENDING STARTS HERE, not when the request leaves. The debounce window is part of the
        // gap the race lived in: from the instant a new identity is asked about, nothing on screen has
        // a verdict about it, and that includes the FIRST question asked on mount. Per-selection by
        // construction — it is keyed off the same identity change that withdraws the previous answer.
        this._setPending(true);

        if (had_previous_key) {
            this._onAvailability(null);
        }
    }

    // Back to "no opinion yet": the pending request is cancelled, any in-flight answer loses its
    // ticket so it can never land, and the key is cleared so the next real identity is asked about
    // afresh — and, having no previous key, emits no second `null` of its own.
    _forget() {
        clearTimeout(this._timer);
        this._request_id++;

        // No question is outstanding any more, so nothing is pending. Withholding the button here
        // would strand it: nothing is coming to clear it.
        this._setPending(false);

        if (this._key === null) {
            return;
        }

        this._key = null;
        this._onAvailability(null);
    }

    _fetch(reservation_id, invoice_id) {
        // A slow answer for identity A must never overwrite a newer answer for identity B: every
        // request takes a ticket, and only the latest ticket is allowed to report.
        const request_id = ++this._request_id;
        const apply = (availability) => {
            if (this._disposed || request_id !== this._request_id) {
                return;
            }

            // CLEARED ON BOTH ARMS — `apply` is the single exit of `.then` AND `.catch`, so a read that
            // fails leaves pending exactly as surely as one that succeeds. A pending flag that only
            // cleared on success would disable the button forever on the first network blip.
            this._setPending(false);
            this._onAvailability(availability);
        };

        // jQuery Deferred: `.then`/`.catch` only — it has `.always`, never `.finally`.
        Ajax.getAccountPaymentAvailability(reservation_id, invoice_id)
            .then((response) => apply(parseAccountPaymentAvailability(response)))
            // FAIL OPEN: a failed read reports "available" — see the note above.
            .catch(() => apply(AVAILABLE));
    }

    dispose() {
        this._disposed = true;
        clearTimeout(this._timer);
    }
}
